/* Size Advisor — <size-advisor> Web Component. Zero dependencies. */

const STORAGE_KEY = 'shopify_size_profile';

const METERS = {
  height: { key: 'heightCm', unit: 'cm', min: 140, max: 210 },
  weight: { key: 'weightKg', unit: 'kg', min: 40, max: 150 },
};

const DEFAULTS = {
  heightCm: 168,
  weightKg: 62,
  bust: 'average',
  stomach: 'average',
  hips: 'average',
  fitPreference: 'designed',
};

/* ---------- Model obliczeniowy (czyste funkcje) ---------- */

// Szacunek obwodów z wzrostu i BMI względem przeciętnej sylwetki; k = [cm na 1 pkt BMI, cm na 1 cm wzrostu]
const BODY_MODEL = {
  women: { ref: { height: 166, bmi: 22 }, base: { bust: 88, waist: 71, hips: 97 }, k: { bust: [2.4, 0.25], waist: [3.0, 0.2], hips: [2.2, 0.3] } },
  men: { ref: { height: 178, bmi: 24 }, base: { bust: 98, waist: 85, hips: 98 }, k: { bust: [2.3, 0.25], waist: [3.0, 0.2], hips: [2.0, 0.25] } },
};

const SHAPE_ADJUST = {
  bust: { slimmer: -4, average: 0, broader: 4 },
  stomach: { flatter: -4, average: 0, rounder: 5 },
  hips: { narrower: -4, average: 0, fuller: 5 },
};

// Docelowe odchylenie (w połówkach przedziału rozmiaru): dodatnie = ciało większe od środka rozmiaru = bliżej ciała
const FIT_TARGET = { close: 0.4, designed: 0, relaxed: -0.4 };
const WEIGHTS = { bust: 0.3, waist: 0.3, hips: 0.25, length: 0.15 };

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
const norm = (v) => String(v ?? '').trim().toLowerCase();

export function estimateBody({ gender, heightCm, weightKg, bust, stomach, hips }) {
  const model = BODY_MODEL[gender] ?? BODY_MODEL.women;
  const bmi = weightKg / (heightCm / 100) ** 2;
  const dBmi = bmi - model.ref.bmi;
  const dHeight = heightCm - model.ref.height;
  const at = (key) => model.base[key] + dBmi * model.k[key][0] + dHeight * model.k[key][1];

  return {
    bmi,
    height: heightCm,
    bust: at('bust') + (SHAPE_ADJUST.bust[bust] ?? 0),
    waist: at('waist') + (SHAPE_ADJUST.stomach[stomach] ?? 0),
    hips: at('hips') + (SHAPE_ADJUST.hips[hips] ?? 0),
  };
}

const deviation = (value, range) => {
  if (!Array.isArray(range)) return null;
  const [min, max] = range;
  return (value - (min + max) / 2) / Math.max((max - min) / 2, 2);
};

/** @param chart Array<[size, {bust,waist,hips,height}]> w kolejności rozmiarów */
export function rankSizes(body, chart, fitPreference = 'designed') {
  const target = FIT_TARGET[fitPreference] ?? 0;

  const sizes = chart.map(([size, m]) => {
    const dims = {
      bust: deviation(body.bust, m.bust),
      waist: deviation(body.waist, m.waist),
      hips: deviation(body.hips, m.hips),
      length: deviation(body.height, m.height),
    };
    let sum = 0;
    let weight = 0;
    for (const [key, w] of Object.entries(WEIGHTS)) {
      if (dims[key] == null) continue;
      sum += w * Math.abs(dims[key] - (key === 'length' ? 0 : target));
      weight += w;
    }
    const score = weight ? sum / weight : Infinity;
    return { size, dims, score, match: Math.round(clamp(97 - score * 20, 55, 98)) };
  });

  const best = sizes.reduce((a, b) => (b.score < a.score ? b : a), sizes[0]);
  return { best, sizes };
}

/* ---------- Integracja ze stroną produktu ---------- */

function productScope(host) {
  const form =
    host.closest('form[action*="/cart/add"]') ||
    host.closest('[data-section-id], .shopify-section')?.querySelector('form[action*="/cart/add"]') ||
    document.querySelector('form[action*="/cart/add"]');
  return { form, root: form?.closest('[data-section-id], .shopify-section') || form || document };
}

/** Zaznacza wariant o danym rozmiarze: przyciski motywu → radio → select. */
export function selectProductVariant(size, host) {
  const { root } = productScope(host);
  const target = norm(size);

  const buttons = [...root.querySelectorAll('[data-variant-size]')].filter((b) => norm(b.dataset.variantSize) === target);
  const button = buttons.find((b) => b.offsetParent !== null) || buttons[0];
  if (button) {
    button.click();
    return true;
  }

  const radio = [...root.querySelectorAll('input[type="radio"]')].find((r) => norm(r.value) === target);
  if (radio) {
    radio.checked = true;
    radio.dispatchEvent(new Event('input', { bubbles: true }));
    radio.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  for (const select of root.querySelectorAll('select')) {
    const option = [...select.options].find((o) => norm(o.value) === target || norm(o.textContent) === target);
    if (option) {
      select.value = option.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }
  }
  return false;
}

/* ---------- Komponent ---------- */

const fill = (template, values) => template.replace(/\{(\w+)\}/g, (_, k) => values[k] ?? '');
const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
const nextFrame = () => new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

function resolveChart(genderChart = {}, productSizes = []) {
  const byKey = new Map(Object.entries(genderChart).map(([size, m]) => [norm(size), m]));
  const matched = productSizes.filter((s) => byKey.has(norm(s))).map((s) => [s, byKey.get(norm(s))]);
  return matched.length ? matched : Object.entries(genderChart);
}

function readProfile() {
  try {
    const p = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (!p || !p.height || !p.weight) return null;
    return { ...p, heightCm: p.height, weightKg: p.weight };
  } catch {
    return null;
  }
}

class SizeAdvisor extends HTMLElement {
  #ac;

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    const config = JSON.parse(this.querySelector('[data-sa-config]').textContent);
    this.t = config.i18n;
    this.label = config.label;
    this.chart = resolveChart(config.chart[config.gender] ?? config.chart.women, config.sizes);

    const saved = readProfile();
    this.hasProfile = Boolean(saved?.calculatedSize);
    this.state = { ...DEFAULTS, ...saved, gender: config.gender };
    this.step = 1;

    // Dialog poza formularzem produktu: inputy doradcy nie trafią do /cart/add, a transformy rodziców nie psują pozycji.
    document.body.append(this.dialog);

    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.querySelector('[data-sa-open]').addEventListener('click', () => this.open(), { signal });
    this.dialog.addEventListener('click', (e) => this.#onClick(e), { signal });
    this.dialog.addEventListener('input', (e) => this.#onInput(e), { signal });
    this.dialog.addEventListener('change', (e) => this.#onInput(e), { signal });
    this.dialog.addEventListener('cancel', (e) => { e.preventDefault(); this.close(); }, { signal });

    this.#renderTrigger();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    if (this.dialog) this.append(this.dialog);
  }

  $(selector) {
    return this.dialog.querySelector(selector);
  }

  open() {
    this.#sync();
    if (this.hasProfile) {
      this.#renderResult();
      this.#goTo(4);
    } else {
      this.#goTo(1);
    }
    this.dialog.showModal();
  }

  close() {
    const d = this.dialog;
    if (!d.open) return;
    this.#note('');
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return d.close();

    d.classList.add('is-closing');
    const done = () => { clearTimeout(timer); d.classList.remove('is-closing'); d.close(); };
    const timer = setTimeout(done, 400);
    d.addEventListener('animationend', done, { once: true });
  }

  /* ----- zdarzenia ----- */

  #onClick(e) {
    if (e.target === this.dialog) return this.close();
    const btn = e.target.closest('[data-sa-action]');
    if (!btn) return;

    switch (btn.dataset.saAction) {
      case 'close': return this.close();
      case 'back': return this.#goTo(Math.max(1, this.step - 1));
      case 'next':
        if (this.step === 3) {
          this.#renderResult();
          this.#save();
        }
        return this.#goTo(this.step + 1);
      case 'nudge':
        this.#setDisplay(btn.dataset.field, this.#display(btn.dataset.field) + Number(btn.dataset.dir));
        return this.#sync();
      case 'restart': return this.#goTo(1);
      case 'select':
        if (this.#applyVariant()) this.close();
        return;
      case 'add': return this.#addToCart();
    }
  }

  #onInput(e) {
    const el = e.target;
    if (el.dataset.saRange) {
      this.#setDisplay(el.dataset.saRange, Number(el.value));
    } else if (el.type === 'radio' && el.dataset.saField) {
      if (el.dataset.saField === 'compare') return this.#compare(el.value);
      this.state[el.dataset.saField] = el.value;
    } else {
      return;
    }
    this.#sync();
  }

  /* ----- wzrost / waga ----- */

  #display(field) {
    return Math.round(this.state[METERS[field].key]);
  }

  #setDisplay(field, value) {
    const { key, min, max } = METERS[field];
    this.state[key] = clamp(Math.round(value), min, max);
  }

  /* ----- render ----- */

  #compute(overrides = {}) {
    const p = { ...this.state, ...overrides };
    return this.chart.length ? rankSizes(estimateBody(p), this.chart, p.fitPreference) : null;
  }

  #sync() {
    for (const radio of this.dialog.querySelectorAll('input[type="radio"][data-sa-field]')) {
      if (radio.dataset.saField !== 'compare') radio.checked = this.state[radio.dataset.saField] === radio.value;
    }

    for (const [field, { unit, min, max }] of Object.entries(METERS)) {
      const range = this.$(`[data-sa-range="${field}"]`);
      const value = this.#display(field);
      const text = `${value} ${unit}`;
      Object.assign(range, { min, max, step: 1 });
      range.value = value;
      range.setAttribute('aria-valuetext', text);
      range.style.setProperty('--sa-fill', `${((value - min) / (max - min)) * 100}%`);
      this.$(`[data-sa-out="${field}"]`).textContent = text;
    }

    const estimate = this.#compute({ bust: 'average', stomach: 'average', hips: 'average', fitPreference: 'designed' });
    this.$('[data-sa-estimate]').textContent = estimate?.best.size ?? '—';
  }

  #goTo(step) {
    this.step = step;
    const isResult = step === 4;

    this.dialog.querySelectorAll('[data-sa-step]').forEach((panel) => {
      panel.hidden = Number(panel.dataset.saStep) !== step;
    });
    this.$('[data-sa-progress]').hidden = isResult;
    this.$('[data-sa-step-num]').textContent = `0${Math.min(step, 3)}`;
    this.dialog.querySelectorAll('.sa-progress__bars span').forEach((bar, i) => bar.classList.toggle('is-active', i < step));

    this.$('[data-sa-action="back"]').hidden = step === 1;
    const next = this.$('[data-sa-action="next"]');
    next.hidden = isResult;
    next.textContent = step === 3 ? this.t.show_result : this.t.next;
    this.$('[data-sa-actions]').hidden = !isResult;
    this.#note('');

    this.$('[data-sa-body]').scrollTop = 0;
    if (this.dialog.open) this.$(`[data-sa-step="${step}"] [tabindex="-1"]`)?.focus({ preventScroll: true });
  }

  #renderResult() {
    this.result = this.#compute();
    if (!this.result) return;
    const { best, sizes } = this.result;

    this.$('[data-sa-result-size]').textContent = best.size;
    this.$('[data-sa-result-match]').textContent = fill(this.t.match, best);
    this.$('[data-sa-lead]').textContent = fill(this.t.lead, best);

    const ring = this.$('[data-sa-ring]');
    const circumference = 2 * Math.PI * ring.r.baseVal.value;
    ring.style.strokeDasharray = circumference;
    ring.style.strokeDashoffset = circumference;
    nextFrame().then(() => { ring.style.strokeDashoffset = circumference * (1 - best.match / 100); });

    const uid = this.dialog.getAttribute('aria-labelledby');
    const compare = this.$('[data-sa-compare]');
    compare.style.setProperty('--sa-count', sizes.length);
    compare.innerHTML = sizes.map(({ size }) => `
      <label class="sa-compare__opt">
        <input class="sa-vh" type="radio" name="${uid}-compare" value="${escapeHtml(size)}" data-sa-field="compare">
        <span>${escapeHtml(size)}</span>
        ${size === best.size ? `<small class="sa-compare__badge">${this.t.recommended}</small>` : ''}
      </label>`).join('');

    const bustLabel = this.state.gender === 'men' ? this.t.bust_men : this.t.bust_women;
    const labels = { bust: bustLabel, waist: this.t.waist, hips: this.t.hips, length: this.t.length };
    this.$('[data-sa-bars]').innerHTML = Object.keys(labels)
      .filter((key) => best.dims[key] != null)
      .map((key) => `
        <li class="sa-bar" data-dim="${key}">
          <div class="sa-bar__head"><span>${labels[key]}</span><span class="sa-bar__verdict"></span></div>
          <div class="sa-bar__track"><span class="sa-bar__marker"></span></div>
        </li>`).join('');

    nextFrame().then(() => this.#compare(best.size));
    this.#renderTrigger();
  }

  #compare(size) {
    const entry = this.result?.sizes.find((s) => s.size === size);
    if (!entry) return;
    this.compareSize = size;

    const radios = [...this.dialog.querySelectorAll('[data-sa-field="compare"]')];
    radios.forEach((r) => { r.checked = r.value === size; });
    this.$('[data-sa-compare]').style.setProperty('--sa-index', radios.findIndex((r) => r.checked));
    this.$('[data-sa-compare-match]').textContent = fill(this.t.compare_match, entry);

    for (const row of this.dialog.querySelectorAll('.sa-bar')) {
      const d = entry.dims[row.dataset.dim];
      const isLength = row.dataset.dim === 'length';
      row.querySelector('.sa-bar__marker').style.setProperty('--sa-pos', `${clamp(50 - d * 22, 6, 94)}%`);
      row.querySelector('.sa-bar__verdict').textContent =
        d > 0.6 ? (isLength ? this.t.shorter : this.t.snug)
        : d < -0.6 ? (isLength ? this.t.longer : this.t.roomy)
        : this.t.ideal;
    }

    this.$('[data-sa-action="select"]').textContent = fill(this.t.select_only, { size });
    this.$('[data-sa-action="add"]').textContent = fill(this.t.add_to_bag, { size });
    this.#note('');
  }

  #renderTrigger() {
    const label = this.querySelector('[data-sa-trigger-label]');
    const size = this.hasProfile ? (this.result ?? this.#compute())?.best.size : null;
    label.innerHTML = size
      ? `${escapeHtml(fill(this.t.trigger_saved, { size }))} <span class="sa-trigger__edit">${this.t.edit}</span>`
      : escapeHtml(this.label);
  }

  #note(message) {
    const note = this.$('[data-sa-note]');
    note.textContent = message;
    note.hidden = !message;
  }

  /* ----- akcje ----- */

  #save() {
    if (!this.result) return;
    const s = this.state;
    const profile = {
      height: this.#display('height'),
      weight: this.#display('weight'),
      bust: s.bust,
      stomach: s.stomach,
      hips: s.hips,
      fitPreference: s.fitPreference,
      calculatedSize: this.result.best.size,
      matchPercentage: this.result.best.match,
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
    } catch { /* tryb prywatny / zablokowany storage — działamy bez zapisu */ }
    this.hasProfile = true;
    this.#renderTrigger();
  }

  #applyVariant() {
    const size = this.compareSize;
    const ok = selectProductVariant(size, this);
    if (!ok) this.#note(fill(this.t.not_found, { size }));
    return ok;
  }

  async #addToCart() {
    if (!this.#applyVariant()) return;
    await nextFrame(); // pozwól motywowi zaktualizować ukryte pole id wariantu
    const submit = productScope(this).form?.querySelector('[type="submit"]');
    if (!submit || submit.disabled) return this.#note(fill(this.t.unavailable, { size: this.compareSize }));
    this.close();
    submit.click();
  }
}

if (!customElements.get('size-advisor')) customElements.define('size-advisor', SizeAdvisor);
